import { Color } from "src/games/shared/ColorValue";
import { TiledGameObject } from "src/games/shared/phaserHelpers/HandyTypes";
import { PhaserButtonConfig, PhaserButton } from "src/games/shared/Button";
import { GameState } from "src/games/FitFiveGame/objects/GameState";
import TranslateApi from "../../../utils/TranslateHelper";
import initializeInteractiveCursor from "../../shared/InteractiveCursorUtil";
import { Question, NPCResponse } from "src/games/FitFiveGame/objects/NPC";
import AudioTranslations from "src/constants/AudioTranslations";
import { numKeys } from "src/constants/KeyboardNumKeys";

export class TalkToNPCScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    background: Phaser.GameObjects.Image;
    map: Phaser.Tilemaps.Tilemap;
    npcArea: TiledGameObject;
    dialogArea: TiledGameObject;
    buttonArea: TiledGameObject;
    padding: number = 20;
    buttonYCoordinate: number;
    options: Question[];

    constructor() {
        super({
            key: "TalkToNPCScene" + Math.random()
        });
    }

    preload(): void {
        this.load.tilemapTiledJSON('character_focused', require("/src/assets/qci/maps/fitFiveGame/character_focused.json"));
        var keys = [
            "FitFive.Phaser.NPCS.1.dialog.1", "FitFive.Phaser.NPCS.1.dialog.2", "FitFive.Phaser.NPCS.1.dialog.3", "FitFive.Phaser.NPCS.1.dialog.4",
            "FitFive.Phaser.NPCS.2.dialog.1", "FitFive.Phaser.NPCS.2.dialog.2", "FitFive.Phaser.NPCS.2.dialog.3", "FitFive.Phaser.NPCS.2.dialog.4",
            "FitFive.Phaser.NPCS.3.dialog.1", "FitFive.Phaser.NPCS.3.dialog.2", "FitFive.Phaser.NPCS.3.dialog.3", "FitFive.Phaser.NPCS.3.dialog.4",
            "FitFive.Phaser.NPCS.4.dialog.1", "FitFive.Phaser.NPCS.4.dialog.2", "FitFive.Phaser.NPCS.4.dialog.3", "FitFive.Phaser.NPCS.4.dialog.4",
            "FitFive.Phaser.NPCS.5.dialog.1", "FitFive.Phaser.NPCS.5.dialog.2", "FitFive.Phaser.NPCS.5.dialog.3", "FitFive.Phaser.NPCS.5.dialog.4",
        ];

        keys.forEach((key) => {
            this.load.audio(key, AudioTranslations.FindByKeyAndLanguage(key, TranslateApi.currentLanguage.code).url);
        });
    }

    create(): void {
        this.enableMainSceneInput(false);
        this.initialize();
        this.generateScene();
        initializeInteractiveCursor(this);
    }

    initialize() {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.map = this.make.tilemap({ key: 'character_focused' });
        this.options = this.getOptions();
    }

    generateScene() {
        this.background = this.add.image(0, 0, GameState.current.currentNPC.backgroundAssetKey);
        this.background.setOrigin(0);
        this.background.setDisplaySize(this.gameWidth, this.gameHeight);
        var i = 0;

        this.map.getObjectLayer("Object").objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "npc") {
                this.npcArea = ob;
                var npc = this.add.image(ob.x, ob.y, 'assets', GameState.current.currentNPC.assetKey);
                npc.setOrigin(0);
                npc.setDisplaySize(ob.width, ob.height);
            }
            else if (ob.name === "dialog") {
                this.dialogArea = ob;
                this.addDialog();
            }
            else if (ob.name.startsWith("button")) {
                if (this.options[i]) {
                    this.createQuestionButton(ob, i);
                    i++;
                }
            }
        });
    }

    addDialog(): void {
        var ob = this.dialogArea;
        var npcResponse = GameState.current.currentNPC.responses.find((r: NPCResponse) => r.questionId === GameState.current.question.id);
        var translatedDialog = TranslateApi.translate(npcResponse.textID);
        var fSize = translatedDialog.length < 670 ? 16 : translatedDialog.length < 780 ? 15 : 14;
        var dialogStyle = { padding: this.padding, fontSize: fSize, fontFamily: "Roboto Mono, Arial", color: "#F8E71C" };
        let dialog = this.add.text(ob.x, ob.y, translatedDialog, dialogStyle);
        dialog.setOrigin(0);
        dialog.setWordWrapWidth(ob.width - 2 * this.padding, true);
        dialog.setDepth(10);

        var outline = this.add.image(ob.x - 5, ob.y - 5, "pixel");
        outline.setOrigin(0);
        outline.setTint(Color.PLANET_COLORS.get("S"));
        outline.setDisplaySize(ob.width + 10, ob.height + 10);

        let dialogBackground = this.add.image(ob.x, ob.y, "pixel");
        dialogBackground.setOrigin(0);
        dialogBackground.setTint(0x000000);
        dialogBackground.setDepth(0);
        dialogBackground.setDisplaySize(ob.width, ob.height);

        var audio = this.sound.add(npcResponse.textID);
        audio.play();

    }

    createQuestionButton(ob: TiledGameObject, index : number): void {
        if (ob.name === "button_1") {
            this.buttonYCoordinate = ob.y;
        }
        let question: Question = this.options[index];
        var config = this.getButtonConfig();
        var translatedText = (index + 1) + ' : ' + TranslateApi.translate(question.textID);
        var button = new PhaserButton(this, ob.x, ob.y, ob.width, ob.height, translatedText, config);
        button.setDisplaySize(button.width, button.getIdealButtonHeight());
        if (ob.name !== "button_1") {
            button.setY(this.buttonYCoordinate);
        }
        this.buttonYCoordinate += button.displayHeight + 30;
        button.name = name;
        button.on('buttonClicked', () => {
            this.responseSelected(question);
        });

        let numKey = numKeys[index];
        this.onKeydown(() => {
            this.responseSelected(question);
        }, numKey.keyCode, numKey.altKeyCode);
    }

    responseSelected(question: Question): void {
        this.sound.stopAll();
        if (question.id === 4) {
            // Return to main scene
            GameState.current.response = null;
            GameState.current.question = null;
            this.resetQuestions();
            this.enableMainSceneInput(true);
            GameState.current.currentNPC.talkedTo = true;
            this.scene.remove(this);
            this.scene.resume("MainScene");
        }
        else {
            question.asked = true;
            GameState.current.response = GameState.current.currentNPC.responses.find((r: NPCResponse) => r.questionId === question.id);
            GameState.current.question = question;
            this.scene.remove(this);
            this.scene.add("TalkToNPCScene", TalkToNPCScene, true);
        }
    }

    enableMainSceneInput(enabled: boolean): void {
        this.scene.get("MainScene").children.list.forEach((c) => {
            if (c.input != null) {
                c.input.enabled = enabled;
            }
        });
    }

    resetQuestions(): void {
        Question.QUESTIONS.forEach((q: Question) => {
            q.asked = false;
        });
    }

    getOptions(): Question[] {
        var options = [];

        Question.QUESTIONS.forEach((q: Question) => {
            if (!q.asked && q.id !== 4) {
                options.push(q);
            }
        });

        if (options.length === 0) {
            options.push(Question.QUESTIONS.find((q: Question) => q.id === 4));
        }

        return options;
    }

    getButtonConfig(): PhaserButtonConfig {
        var config = new PhaserButtonConfig();
        config.smartHeight = true;
        config.backgroundColor = Color.KUDER_SECONDARY_BUTTON;
        return config;
    }

    onKeydown(event: Function, ...keys: string[]): void {
        keys.forEach((key: string) => {
            this.input.keyboard.on('keydown_' + key, event);
        });
    }
}




// WEBPACK FOOTER //
// ./src/games/FitFiveGame/scenes/TalkToNPCScene.ts